import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { usePartnerRoles } from '@hooks/usePartnerRoles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartMixed,
  faFile,
  faGear,
  faListRadio,
  faKey,
} from '@fortawesome/pro-regular-svg-icons';
import AllyIcon from '@components/ui/icons/ally_icon_one_color';
import { Avatar, RoleTag, ThemeToggle } from './ui';
import { avatarColorIndex } from './ui/avatar_color';

const Sidebar: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const { displayRole, isAdmin } = usePartnerRoles();
  const location = useLocation();

  // Desktop: profile dropdown at bottom of sidebar
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Mobile: hamburger accordion (nav items only)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Mobile: avatar profile dropdown (independent from hamburger)
  const [isMobileUserOpen, setIsMobileUserOpen] = useState(false);
  const mobileUserRef = useRef<HTMLDivElement>(null);

  // Close desktop dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile user dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (mobileUserRef.current && !mobileUserRef.current.contains(e.target as Node)) {
        setIsMobileUserOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close everything on route change
  useEffect(() => {
    setIsMobileNavOpen(false);
    setIsMobileUserOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const isActiveRoute = (path: string) => location.pathname === path;
  const hiddenForPartnerViewer = displayRole === 'PartnerViewer';

  // Each link now carries a single `hidden` condition — computed right
  // here from the current auth/role state.
  const navLinks = [
    {
      path: '/',
      label: 'User Analytics',
      icon: <FontAwesomeIcon icon={faChartMixed} />,
      hidden: false,
    },
    {
      path: '/voice-demo',
      label: 'Ally Demo',
      icon: <AllyIcon className="ally-icon" />,
      hidden: displayRole === 'PartnerViewer',
    },
    {
      path: '/api-keys',
      label: 'API Keys',
      icon: <FontAwesomeIcon icon={faKey} />,
      hidden: !isAdmin,
    },
    {
      path: '/logs',
      label: 'Logs',
      icon: <FontAwesomeIcon icon={faListRadio} />,
      hidden: false,
    },
    {
      path: '/api-docs',
      label: 'API Docs',
      icon: <FontAwesomeIcon icon={faFile} />,
      hidden: false,
    },
    {
      path: '/account',
      label: 'Account',
      icon: <FontAwesomeIcon icon={faGear} />,
      hidden: false,
    },
  ];

  const visibleNavLinks = navLinks.filter((link) => !link.hidden);

  const colorIndex = avatarColorIndex(user?.sub || user?.email || user?.name || 'user');

  // Shared profile dropdown content
  const profileDropdown = (positionClass: string) => (
    <div className={`sidebar-user-dropdown ${positionClass}`}>
      <div className="sidebar-dropdown-header">
        <p className="sidebar-dropdown-name">{user?.name}</p>
        <p className="sidebar-dropdown-email">{user?.email}</p>
      </div>
      <div className="sidebar-dropdown-divider" />
      <div className="sidebar-dropdown-item sidebar-dropdown-item-theme">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
        <span className="sidebar-dropdown-item-label">Theme</span>
        <ThemeToggle className="sidebar-theme-toggle" />
      </div>
      <div className="sidebar-dropdown-divider" />
      <Link to="/profile" className="sidebar-dropdown-item">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        My Profile
      </Link>
      <button
        type="button"
        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        className="sidebar-dropdown-item sidebar-dropdown-item-danger"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
        Log Out
      </button>
    </div>
  );

  return (
    <aside className={`sidebar ${isMobileNavOpen ? 'sidebar-mobile-open' : ''}`}>
      {/* ── Top bar (mobile) / brand section (desktop) ── */}
      <div className="sidebar-topbar">
        {/* Left: brand + hamburger toggle (mobile only) */}
        <div className="sidebar-brand">
          <Link to="/" className="sidebar-brand-link">
            <span className="sidebar-brand-logo-wrap">
              <img src="/img/ally_icon.svg" alt="Ally Connect" className="sidebar-brand-logo" />
            </span>
            <span className="sidebar-brand-text">Ally Connect</span>
          </Link>
          {/* Hamburger — mobile only, controls nav accordion */}
          <button
            type="button"
            className="sidebar-hamburger-inline"
            onClick={() => {
              setIsMobileNavOpen(!isMobileNavOpen);
              setIsMobileUserOpen(false);
            }}
            aria-label="Toggle navigation"
            aria-expanded={isMobileNavOpen}
          >
            {isMobileNavOpen ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Right: avatar + name + role + chevron — mobile only, controls profile dropdown */}
        {isAuthenticated && user && (
          <div className="sidebar-mobile-controls" ref={mobileUserRef}>
            <button
              type="button"
              className="sidebar-mobile-avatar-btn"
              onClick={() => {
                setIsMobileUserOpen(!isMobileUserOpen);
                setIsMobileNavOpen(false);
              }}
              aria-label="Open profile menu"
              aria-expanded={isMobileUserOpen}
            >
              <Avatar
                size="sm"
                src={user.picture}
                name={user.name || user.email || 'User'}
                colorIndex={colorIndex}
              />
              <div className="sidebar-mobile-user-info">
                <span className="sidebar-mobile-user-name">{user.name || 'User'}</span>
                {displayRole && <RoleTag displayRole={displayRole} />}
              </div>
              <svg
                className={`sidebar-mobile-chevron ${
                  isMobileUserOpen ? 'sidebar-mobile-chevron-open' : ''
                }`}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Profile dropdown — opens below the avatar button */}
            {isMobileUserOpen && profileDropdown('sidebar-user-dropdown-mobile')}
          </div>
        )}
      </div>

      {/* ── Nav accordion (mobile: only when hamburger open; desktop: always) ── */}
      <nav className="sidebar-nav" aria-label="Main navigation">
        {isAuthenticated ? (
          visibleNavLinks.map((link) => (
            <div key={link.path} className="sidebar-nav-wrapper">
              <Link
                to={link.path}
                className={`sidebar-nav-pill ${
                  isActiveRoute(link.path) ? 'sidebar-nav-pill-active' : ''
                }`}
              >
                <span className="sidebar-nav-icon">{link.icon}</span>
                <span className="sidebar-nav-label">{link.label}</span>
              </Link>
            </div>
          ))
        ) : (
          <button
            type="button"
            onClick={() => loginWithRedirect()}
            className="sidebar-login-button"
          >
            Log In
          </button>
        )}
      </nav>

      {/* ── User section — desktop only (bottom of sidebar) ── */}
      {isAuthenticated && user && (
        <div className="sidebar-user-section" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="sidebar-user-button"
            aria-expanded={isUserMenuOpen}
            aria-haspopup="true"
          >
            <Avatar
              size="sm"
              src={user.picture}
              name={user.name || user.email || 'User'}
              colorIndex={colorIndex}
            />
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{user.name || 'User'}</span>
              {displayRole && <RoleTag displayRole={displayRole} />}
            </div>
            <svg
              className={`sidebar-chevron ${isUserMenuOpen ? 'sidebar-chevron-open' : ''}`}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isUserMenuOpen && profileDropdown('')}
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
